import { useCallback, useEffect, useMemo, useState } from "react";

type KanbanColumn = {
  id: string;
  title: string;
  type: "fixed" | "stage";
  stageId?: string;
  archived?: boolean;
};

type ApplicationWithId = { id: string };

interface UseKanbanKeyboardNavOptions {
  columns: KanbanColumn[];
  getColumnApplications: (column: KanbanColumn) => ApplicationWithId[];
  hiddenColumnIds: Set<string>;
  searchQuery: string;
  isModalOpen: boolean;
  onSelect: (applicationId: string) => void;
  onDelete?: (applicationId: string) => void;
  kanbanScrollRef: React.RefObject<HTMLDivElement | null>;
  activeTab: string;
}

export function useKanbanKeyboardNav({
  columns,
  getColumnApplications,
  hiddenColumnIds,
  searchQuery,
  isModalOpen,
  onSelect,
  onDelete,
  kanbanScrollRef,
  activeTab,
}: UseKanbanKeyboardNavOptions) {
  const [keyboardSelectedId, setKeyboardSelectedId] = useState<string | null>(null);

  const visibleColumns = useMemo(() => {
    return columns.filter((col) => {
      if (hiddenColumnIds.has(col.id)) return false;
      if (searchQuery) {
        return getColumnApplications(col).length > 0;
      }
      return true;
    });
  }, [columns, hiddenColumnIds, searchQuery, getColumnApplications]);

  const grid = useMemo(() => {
    return visibleColumns.map((col) => ({
      column: col,
      apps: getColumnApplications(col),
    }));
  }, [visibleColumns, getColumnApplications]);

  const findPosition = useCallback(
    (appId: string): { colIdx: number; rowIdx: number } | null => {
      for (let colIdx = 0; colIdx < grid.length; colIdx++) {
        const rowIdx = grid[colIdx].apps.findIndex((a) => a.id === appId);
        if (rowIdx !== -1) return { colIdx, rowIdx };
      }
      return null;
    },
    [grid],
  );

  useEffect(() => {
    if (activeTab !== "kanban") return;
    if (keyboardSelectedId) {
      const pos = findPosition(keyboardSelectedId);
      if (!pos) {
        const firstCol = grid.find((g) => g.apps.length > 0);
        setKeyboardSelectedId(firstCol?.apps[0]?.id ?? null);
      }
    } else {
      const firstCol = grid.find((g) => g.apps.length > 0);
      setKeyboardSelectedId(firstCol?.apps[0]?.id ?? null);
    }
  }, [grid, activeTab, findPosition, keyboardSelectedId]);

  const scrollCardIntoView = useCallback(
    (appId: string) => {
      const container = kanbanScrollRef.current;
      if (!container) return;
      const cardEl = container.querySelector(`[data-testid="kanban-card-${appId}"]`);
      if (!cardEl) return;

      const columnEl = cardEl.closest('[data-testid^="kanban-column-"]');
      if (columnEl) {
        const containerRect = container.getBoundingClientRect();
        const colRect = columnEl.getBoundingClientRect();
        if (colRect.left < containerRect.left) {
          container.scrollLeft -= containerRect.left - colRect.left + 16;
        } else if (colRect.right > containerRect.right) {
          container.scrollLeft += colRect.right - containerRect.right + 16;
        }
      }

      cardEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
    },
    [kanbanScrollRef],
  );

  useEffect(() => {
    if (activeTab !== "kanban" || isModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const tag = target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable) {
        return;
      }

      if (
        ![
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Enter",
          "Backspace",
          "Delete",
        ].includes(e.key)
      ) {
        return;
      }

      e.preventDefault();

      if (e.key === "Enter" && keyboardSelectedId) {
        onSelect(keyboardSelectedId);
        return;
      }

      if ((e.key === "Backspace" || e.key === "Delete") && keyboardSelectedId) {
        onDelete?.(keyboardSelectedId);
        return;
      }

      const pos = keyboardSelectedId ? findPosition(keyboardSelectedId) : null;
      if (!pos) {
        const firstCol = grid.find((g) => g.apps.length > 0);
        if (firstCol?.apps[0]) {
          setKeyboardSelectedId(firstCol.apps[0].id);
          scrollCardIntoView(firstCol.apps[0].id);
        }
        return;
      }

      let nextId: string | null = null;

      if (e.key === "ArrowDown") {
        const col = grid[pos.colIdx];
        if (pos.rowIdx < col.apps.length - 1) {
          nextId = col.apps[pos.rowIdx + 1].id;
        }
      } else if (e.key === "ArrowUp") {
        if (pos.rowIdx > 0) {
          nextId = grid[pos.colIdx].apps[pos.rowIdx - 1].id;
        }
      } else if (e.key === "ArrowRight") {
        for (let i = pos.colIdx + 1; i < grid.length; i++) {
          if (grid[i].apps.length > 0) {
            const rowIdx = Math.min(pos.rowIdx, grid[i].apps.length - 1);
            nextId = grid[i].apps[rowIdx].id;
            break;
          }
        }
      } else if (e.key === "ArrowLeft") {
        for (let i = pos.colIdx - 1; i >= 0; i--) {
          if (grid[i].apps.length > 0) {
            const rowIdx = Math.min(pos.rowIdx, grid[i].apps.length - 1);
            nextId = grid[i].apps[rowIdx].id;
            break;
          }
        }
      }

      if (nextId) {
        setKeyboardSelectedId(nextId);
        scrollCardIntoView(nextId);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeTab,
    isModalOpen,
    keyboardSelectedId,
    grid,
    findPosition,
    onSelect,
    onDelete,
    scrollCardIntoView,
  ]);

  return { keyboardSelectedId, setKeyboardSelectedId };
}
